import React from 'react';
import Icon from '../../../components/AppIcon';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const GameHistory = ({ 
  gameHistory = [], 
  className = "" 
}) => {
  // Mock game history data
  const mockHistory = [
    {
      id: 1,
      gameType: 'reaction-time',
      gameName: 'Reaction Time Test',
      score: 245,
      unit: 'ms',
      completedAt: '2024-08-24T10:30:00Z',
      rating: 'Good',
      attempts: 5
    },
    {
      id: 2,
      gameType: 'reaction-time',
      gameName: 'Reaction Time Test',
      score: 232,
      unit: 'ms',
      completedAt: '2024-08-23T14:15:00Z',
      rating: 'Good',
      attempts: 5
    },
    {
      id: 3,
      gameType: 'memory',
      gameName: 'Memory Sequence',
      score: 8,
      unit: 'correct',
      completedAt: '2024-08-22T16:45:00Z',
      rating: 'Excellent',
      attempts: 10
    },
    {
      id: 4,
      gameType: 'reaction-time',
      gameName: 'Reaction Time Test',
      score: 267,
      unit: 'ms',
      completedAt: '2024-08-21T11:20:00Z',
      rating: 'Average',
      attempts: 5
    },
    {
      id: 5,
      gameType: 'attention',
      gameName: 'Focus Challenge',
      score: 85,
      unit: '%',
      completedAt: '2024-08-20T09:30:00Z',
      rating: 'Good',
      attempts: 1
    }
  ];

  const historyData = gameHistory?.length > 0 ? gameHistory : mockHistory;

  // Prepare chart data for reaction time games
  const reactionTimeData = historyData?.filter(game => game?.gameType === 'reaction-time')?.map(game => ({
      date: new Date(game.completedAt)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      score: game?.score,
      fullDate: game?.completedAt
    }))?.sort((a, b) => new Date(a.fullDate) - new Date(b.fullDate))?.slice(-7); // Last 7 entries

  const getRatingColor = (rating) => {
    switch (rating?.toLowerCase()) {
      case 'excellent': return 'text-success bg-success/10';
      case 'good': return 'text-primary bg-primary/10';
      case 'average': return 'text-warning bg-warning/10';
      case 'needs practice': return 'text-error bg-error/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const getGameIcon = (gameType) => {
    switch (gameType) {
      case 'reaction-time': return 'Zap';
      case 'memory': return 'Brain';
      case 'attention': return 'Eye';
      default: return 'Gamepad2';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (historyData?.length === 0) {
    return (
      <div className={`bg-card border border-border rounded-lg p-8 text-center ${className}`}>
        <Icon name="BarChart3" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-foreground mb-2">No Game History</h3>
        <p className="text-muted-foreground">
          Complete some cognitive games to see your performance history and progress tracking.
        </p>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Performance Chart */}
      {reactionTimeData?.length > 1 && (
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">Reaction Time Progress</h3>
            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
              <Icon name="TrendingDown" size={16} />
              <span>Lower is better</span>
            </div>
          </div>
          
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={reactionTimeData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis 
                  dataKey="date" 
                  stroke="var(--color-muted-foreground)"
                  fontSize={12}
                />
                <YAxis 
                  stroke="var(--color-muted-foreground)"
                  fontSize={12}
                  label={{ value: 'Reaction Time (ms)', angle: -90, position: 'insideLeft' }}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'var(--color-card)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '8px',
                    color: 'var(--color-foreground)'
                  }}
                  formatter={(value) => [`${value}ms`, 'Reaction Time']}
                />
                <Line 
                  type="monotone" 
                  dataKey="score" 
                  stroke="var(--color-primary)" 
                  strokeWidth={2}
                  dot={{ fill: 'var(--color-primary)', strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, stroke: 'var(--color-primary)', strokeWidth: 2 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
      {/* Recent Games List */}
      <div className="bg-card border border-border rounded-lg">
        <div className="p-6 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground">Recent Games</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Your latest cognitive assessment results
          </p>
        </div>
        
        <div className="divide-y divide-border">
          {historyData?.slice(0, 10)?.map((game) => (
            <div key={game?.id} className="p-4 hover:bg-muted/50 transition-colors duration-150">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center">
                    <Icon 
                      name={getGameIcon(game?.gameType)} 
                      size={20} 
                      color="var(--color-muted-foreground)"
                    />
                  </div>
                  
                  <div>
                    <div className="font-medium text-foreground">{game?.gameName}</div>
                    <div className="text-sm text-muted-foreground">
                      {formatDate(game?.completedAt)} • {game?.attempts} attempts
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <div className="text-right">
                    <div className="font-semibold text-foreground">
                      {game?.score}{game?.unit}
                    </div>
                    <div className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${getRatingColor(game?.rating)}`}>
                      {game?.rating}
                    </div>
                  </div>
                  
                  <Icon name="ChevronRight" size={16} color="var(--color-muted-foreground)" />
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {historyData?.length > 10 && (
          <div className="p-4 border-t border-border text-center">
            <button className="text-sm text-primary hover:text-primary/80 font-medium transition-colors duration-150">
              View All History ({historyData?.length} games)
            </button>
          </div>
        )}
      </div>
      {/* Performance Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-card border border-border rounded-lg p-4 text-center">
          <Icon name="Target" size={24} color="var(--color-primary)" className="mx-auto mb-2" />
          <div className="text-2xl font-bold text-foreground">
            {historyData?.length}
          </div>
          <div className="text-sm text-muted-foreground">Games Completed</div>
        </div>
        
        <div className="bg-card border border-border rounded-lg p-4 text-center">
          <Icon name="Calendar" size={24} color="var(--color-accent)" className="mx-auto mb-2" />
          <div className="text-2xl font-bold text-foreground">
            {Math.ceil((Date.now() - new Date(historyData[historyData.length - 1]?.completedAt || Date.now())) / (1000 * 60 * 60 * 24))}
          </div>
          <div className="text-sm text-muted-foreground">Days Active</div>
        </div>
        
        <div className="bg-card border border-border rounded-lg p-4 text-center">
          <Icon name="TrendingUp" size={24} color="var(--color-success)" className="mx-auto mb-2" />
          <div className="text-2xl font-bold text-foreground">
            {reactionTimeData?.length > 1 && reactionTimeData?.[0]?.score > reactionTimeData?.[reactionTimeData?.length - 1]?.score ? '+' : ''}
            {reactionTimeData?.length > 1 ? 
              Math.round(((reactionTimeData?.[0]?.score - reactionTimeData?.[reactionTimeData?.length - 1]?.score) / reactionTimeData?.[0]?.score) * 100) : 0}%
          </div>
          <div className="text-sm text-muted-foreground">Improvement</div>
        </div>
      </div>
    </div>
  );
};

export default GameHistory;